const DEFAULT_ONSET_PAIR_WINDOW_SECONDS = 0.12;

const numericTimestamp = note => Number(
  note?.sourceTimestamp ?? note?.timestamp ?? 0
);

export const prepareCompletedNotesForClassification = notes => (
  notes
    .map((note, index) => ({
      note: note.note,
      velocity: note.velocity,
      timestamp: numericTimestamp(note),
      duration: Math.max(0.001, Number(note.duration || 200) / 1000),
      channel: note.channel || 0,
      hand: note.hand || null,
      finger: note.finger || null,
      event_type: 'note_on',
      _displayOrder: index
    }))
    .sort((left, right) => (
      left.timestamp - right.timestamp || left._displayOrder - right._displayOrder
    ))
    .map(({ _displayOrder, ...note }) => note)
);

export const getLiveClassificationPlan = ({
  notes,
  lastRequestedCount,
  sequenceLength
}) => {
  const sequenceSize = Math.max(1, Number(sequenceLength) || 1);
  const minimumNotes = sequenceSize * 2;
  const noteCount = notes.length;

  if (
    noteCount < minimumNotes ||
    noteCount <= lastRequestedCount
  ) {
    return null;
  }

  const preparedNotes = prepareCompletedNotesForClassification(notes);
  return {
    requestedCount: noteCount,
    notes: preparedNotes.slice(-minimumNotes)
  };
};

export const reconcileParallelHandPairs = (
  notes,
  {
    onsetWindowSeconds = DEFAULT_ONSET_PAIR_WINDOW_SECONDS,
    minimumPairGroups = 4,
    minimumCoverage = 0.5
  } = {}
) => {
  if (!Array.isArray(notes) || notes.length === 0) {
    return [];
  }

  const predictedHands = new Set(notes.map(note => note.hand));
  if (!predictedHands.has('left') || !predictedHands.has('right')) {
    return notes.map(note => ({ ...note }));
  }

  const ordered = notes
    .map((note, index) => ({ note, index }))
    .sort((left, right) => (
      numericTimestamp(left.note) - numericTimestamp(right.note) ||
      left.index - right.index
    ));
  const onsetGroups = [];

  for (const entry of ordered) {
    const current = onsetGroups[onsetGroups.length - 1];
    if (
      !current ||
      numericTimestamp(entry.note) - numericTimestamp(current[0].note) >
        onsetWindowSeconds
    ) {
      onsetGroups.push([entry]);
    } else {
      current.push(entry);
    }
  }

  const pairGroups = onsetGroups.filter(group => (
    group.length === 2 &&
    Math.abs(group[0].note.note - group[1].note.note) >= 5
  ));
  const pairedCoverage = (pairGroups.length * 2) / notes.length;

  if (
    pairGroups.length < minimumPairGroups ||
    pairedCoverage < minimumCoverage
  ) {
    return notes.map(note => ({ ...note }));
  }

  const reconciled = notes.map(note => ({ ...note }));
  pairGroups.forEach(group => {
    const [lower, upper] = [...group].sort(
      (left, right) => left.note.note - right.note.note
    );
    reconciled[lower.index] = {
      ...reconciled[lower.index],
      hand: 'left'
    };
    reconciled[upper.index] = {
      ...reconciled[upper.index],
      hand: 'right'
    };
  });

  return reconciled;
};

export const mergeClassifiedHands = (displayNotes, classifiedNotes) => {
  const available = classifiedNotes.map((note, index) => ({
    note,
    index,
    used: false
  }));

  return displayNotes.map(displayNote => {
    if (displayNote.manuallySet) {
      return displayNote;
    }

    let best = null;
    let bestDistance = Number.POSITIVE_INFINITY;
    available.forEach(candidate => {
      if (
        candidate.used ||
        candidate.note.note !== displayNote.note ||
        Number(candidate.note.channel || 0) !== Number(displayNote.channel || 0)
      ) {
        return;
      }
      const distance = Math.abs(
        numericTimestamp(candidate.note) - numericTimestamp(displayNote)
      );
      if (distance < bestDistance) {
        best = candidate;
        bestDistance = distance;
      }
    });

    if (!best) {
      return displayNote;
    }

    best.used = true;
    return {
      ...displayNote,
      hand: best.note.hand || displayNote.hand || null,
      confidence: best.note.confidence ?? displayNote.confidence ?? null
    };
  });
};
